<template>
  <div
    ref="containerRef"
    :class="['used-by-container', { inactive: !hasItems }]"
    @mouseenter="openTooltip"
    @mouseleave="closeTooltip"
  >
    <div class="items-count">
      {{ count ?? items?.length ?? 0 }}
    </div>

    <Teleport to="body">
      <div
        v-if="tooltipOpen && hasItems"
        class="items-summary"
        :style="tooltipStyle"
        role="tooltip"
        @mouseenter="openTooltip"
        @mouseleave="closeTooltip"
      >
        <div class="label">{{ label }}</div>
        <div
          v-for="(item, index) in previewItems"
          :key="item.id ?? `${item.name}-${index}`"
          class="item"
        >
          {{ item.name }}
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref } from 'vue';

const { items, label, count } = defineProps<{
  items: any;
  label: string;
  count?: number;
}>();

const containerRef = ref<HTMLElement | null>(null);
const tooltipOpen = ref(false);
const tooltipStyle = ref<Record<string, string>>({});

const hasItems = computed(() => (items?.length ?? 0) > 0);

const previewItems = computed(() => items?.slice(0, Math.min(5, items?.length ?? 0)) ?? []);

function updateTooltipPosition() {
  const el = containerRef.value;
  if (!el) return;

  const rect = el.getBoundingClientRect();
  const tooltipWidth = 160;
  let left = rect.right - tooltipWidth;

  left = Math.max(8, Math.min(left, window.innerWidth - tooltipWidth - 8));

  tooltipStyle.value = {
    top: `${rect.bottom + 6}px`,
    left: `${left}px`,
  };
}

// Closing is delayed so the pointer can travel across the gap between the
// count badge and the teleported tooltip without the tooltip flickering
// shut (mouseleave fires on the badge before mouseenter fires on the tooltip).
let closeTimer: ReturnType<typeof setTimeout> | null = null;

function cancelScheduledClose() {
  if (closeTimer !== null) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
}

function openTooltip() {
  if (!hasItems.value) return;
  cancelScheduledClose();
  updateTooltipPosition();
  tooltipOpen.value = true;
}

function closeTooltip() {
  cancelScheduledClose();
  closeTimer = setTimeout(() => {
    tooltipOpen.value = false;
    closeTimer = null;
  }, 150);
}

onBeforeUnmount(() => {
  cancelScheduledClose();
  tooltipOpen.value = false;
});
</script>

<style lang="scss" scoped>
.used-by-container {
  text-align: center;
  vertical-align: middle;
  position: relative;
}

.items-count {
  border-radius: 0.375rem;
  border: 1px solid color-mix(in srgb, var(--bui-used-by-count-text, var(--bui-badge-tag-text, #3a3a44)) 22%, transparent);
  background: var(--bui-used-by-count-fill, var(--bui-badge-tag-fill, #e8e8ec));
  color: var(--bui-used-by-count-text, var(--bui-badge-tag-text, #3a3a44));
  cursor: pointer;
  display: inline-block;
  font-size: 10px;
  font-weight: bold;
  height: 25px;
  line-height: 25px;
  text-align: center;
  min-width: 25px;
  padding-left: 2px;
  padding-right: 2px;
  vertical-align: middle;

  .inactive & {
    background: var(--bui-used-by-count-inactive-fill, var(--bui-badge-neutral-fill, rgba(0, 0, 0, 0.06)));
    color: var(--bui-used-by-count-inactive-text, var(--bui-badge-neutral-text, #5c5c66));
    border-color: color-mix(
      in srgb,
      var(--bui-used-by-count-inactive-text, var(--bui-badge-neutral-text, #5c5c66)) 22%,
      transparent
    );
    cursor: default;
  }
}

.items-summary {
  position: fixed;
  z-index: 10000;
  width: 10rem;
  padding: 0.75rem;
  border-radius: var(--bui-droplet-menu-radius, 8px);
  background: var(--bui-used-by-tooltip-fill, var(--bui-droplet-menu-fill, #ffffff));
  color: var(--bui-used-by-tooltip-text, var(--bui-droplet-menu-text, #2a2a30));
  box-shadow: var(--bui-tbl-card-shadow, 0 8px 24px rgba(0, 0, 0, 0.16));
  text-align: left;
  pointer-events: auto;

  .label {
    font-weight: bold;
    margin-bottom: 0.25rem;
  }

  .item + .item {
    margin-top: 0.125rem;
  }
}
</style>
